/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Modal from '../../../components/Modal/Modal';
import { PrimaryButton, GhostButton } from '../../../styles/ui/Button';
import { formatDocForDisplay } from '../../../utils/format';

// ---------------------------------------------------------------------------
// Última conferência antes de emitir (2026-08-06).
//
// A emissão é irreversível na prática: sair pelo CNPJ errado exige cancelar e
// reemitir, com o cliente já tendo recebido o boleto. Este passo existe para o
// erro ser interceptado ANTES do POST — e por isso a empresa emissora é o
// elemento mais destacado do resumo, não mais uma linha entre outras.
// ---------------------------------------------------------------------------

const Issuer = styled.div`
   border: 2px solid
      ${({ theme }) => theme?.colors?.actionWrite ?? '#4098ef'};
   background: rgba(64, 152, 239, 0.08);
   border-radius: 12px;
   padding: 12px 14px;
   margin-bottom: 14px;

   .eyebrow {
      font-size: 0.64rem;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
   }

   .name {
      font-size: 1.05rem;
      font-weight: 800;
      margin-top: 2px;
   }

   .doc {
      font-size: 0.85rem;
      font-variant-numeric: tabular-nums;
   }

   .owner {
      font-size: 0.72rem;
      color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
      margin-top: 2px;
   }
`;

const Rows = styled.dl`
   margin: 0;
   display: grid;
   grid-template-columns: auto 1fr;
   gap: 6px 14px;

   dt {
      font-size: 0.72rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.02em;
      color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
      align-self: center;
   }

   dd {
      margin: 0;
      font-size: 0.9rem;
      align-self: center;
   }
`;

export default function IssueConfirmModal({
   isOpen,
   onClose,
   onConfirm,
   account,
   summary,
   isBusy = false,
}) {
   const footer = (
      <>
         <GhostButton type="button" onClick={onClose} disabled={isBusy}>
            Revisar
         </GhostButton>
         <PrimaryButton type="button" onClick={onConfirm} disabled={isBusy}>
            {isBusy ? 'Emitindo…' : 'Confirmar e emitir'}
         </PrimaryButton>
      </>
   );

   return (
      <Modal
         isOpen={isOpen}
         onClose={onClose}
         title="Confirmar emissão"
         maxWidth="520px"
         footer={footer}
      >
         <Issuer>
            <div className="eyebrow">Será emitido pela empresa</div>
            <div className="name">{account?.account_name ?? '—'}</div>
            <div className="doc">
               {formatDocForDisplay(account?.account_doc_number)}
            </div>
            {account?.account_owner_name && (
               <div className="owner">{account.account_owner_name}</div>
            )}
         </Issuer>

         <Rows>
            {summary.map((row) => (
               <React.Fragment key={row.label}>
                  <dt>{row.label}</dt>
                  <dd>{row.value}</dd>
               </React.Fragment>
            ))}
         </Rows>
      </Modal>
   );
}

IssueConfirmModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   onClose: PropTypes.func.isRequired,
   onConfirm: PropTypes.func.isRequired,
   account: PropTypes.shape({
      account_name: PropTypes.string,
      account_owner_name: PropTypes.string,
      account_doc_number: PropTypes.string,
   }),
   summary: PropTypes.arrayOf(
      PropTypes.shape({
         label: PropTypes.string.isRequired,
         value: PropTypes.node,
      })
   ).isRequired,
   isBusy: PropTypes.bool,
};
